import { InfoIcon } from '@/assets/icons/info-icons/info';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { TwoColumnWrapper } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { TenantType } from '@trace-8/trace8-schema';
import styled from 'styled-components';
import { DescriptionSection } from '../../../descriptionSection';
import { ProgressBar } from '../../../progressBar';
import { ButtonsControl, ImportWrapper } from '../../../styles';
import { IDescriptionSection } from '../../../types';
import { importInvestorEntitiesUpdateMethodBannerDescriptions, investorEntitiesSteps } from '../../constants';
import { ImportInvestorEntitiesUpdateMethodOptionEnum } from '../../types';

interface GettingStartedProps {
  importSteps: typeof investorEntitiesSteps;
  descriptionSection: IDescriptionSection;
  updateMethod: ImportInvestorEntitiesUpdateMethodOptionEnum;
  selectedAdvisoryFirmId: string;
  advisoryFirmSelectOptions: ISelectValue[];
  handleSelectedUpdateMethod: (updateMethod: ImportInvestorEntitiesUpdateMethodOptionEnum) => void;
  handleSelectedAdvisoryFirmId: (advisoryFirmId: string) => void;
  nextStep: () => void;
  handleClose: () => void;
}

export const GettingStarted = ({
  importSteps,
  descriptionSection,
  updateMethod,
  selectedAdvisoryFirmId,
  advisoryFirmSelectOptions,
  handleSelectedUpdateMethod,
  handleSelectedAdvisoryFirmId,
  nextStep,
  handleClose
}: GettingStartedProps) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const isPlatformManagerUser = user && user?.tenant?.type === TenantType.PLATFORM_MANAGER;

  return (
    <ImportWrapper>
      <ProgressBar importSteps={importSteps} />
      <SectionsWrapper>
        <DescriptionSection {...descriptionSection} />
      </SectionsWrapper>
      <SelectText>Select options to get started</SelectText>
      <OptionsWrapper>
        {isPlatformManagerUser && (
          <>
            <Banner
              icon={<InfoIcon fill={themeVar('context.light')} />}
              title="Please Note"
              description="Ensure that the investor entities are created within the correct Advisory Firm. You can add investors directly to the platform manager."
              color={themeVar('context.light')}
              bgColor={themeVar('context.warning')}
              dividerColor={themeVar('context.light')}
              withBorderRadius
            />
            <TwoColumnWrapper isMobile={isMobile}>
              <Select
                value={selectedAdvisoryFirmId}
                setValue={handleSelectedAdvisoryFirmId}
                options={advisoryFirmSelectOptions}
                label="Advisory Firm"
                size="md"
                tooltip="Assign all newly created Investor Entities to the selected Advisory Firm.  Note that the import file must contain only investors that belong to the selected firm, otherwise duplicate records may be created. "
                tooltipWidth={400}
                isRequiredField
                withSearch
              />
            </TwoColumnWrapper>
          </>
        )}
        <TwoColumnWrapper isMobile={isMobile}>
          <Select
            value={updateMethod || ''}
            setValue={(value) => handleSelectedUpdateMethod(value as ImportInvestorEntitiesUpdateMethodOptionEnum)}
            options={Object.values(ImportInvestorEntitiesUpdateMethodOptionEnum).map((option) => createSimpleSelectOption(option))}
            label="Update Method"
            size="md"
            tooltip="For imported Investor CRM IDs that match an existing Investor Entity, the imported values will overwrite those on the existing Investor Entity record.  New Investor Entities will be created for each unrecognized Investor CRM ID in the import file.  Existing Investor Entities that do not correspond to an Investor CRM ID listed in the Import File will be unchanged."
            tooltipWidth={400}
            isRequiredField
          />
        </TwoColumnWrapper>
        {importInvestorEntitiesUpdateMethodBannerDescriptions[updateMethod] && (
          <Banner
            icon={<InfoIcon fill={themeVar('menu.tab')} />}
            title=""
            description={importInvestorEntitiesUpdateMethodBannerDescriptions[updateMethod]}
            color={themeVar('font.base')}
            bgColor={themeVar('layer.base')}
            dividerColor={themeVar('border.base')}
            withBorderRadius
          />
        )}
      </OptionsWrapper>
      <ButtonsControl>
        <Button styleType="outline" size="lg" onClick={handleClose}>
          Cancel
        </Button>
        <Button size="lg" styleType={!selectedAdvisoryFirmId || !updateMethod ? 'disabled' : 'default'} onClick={nextStep}>
          Continue
        </Button>
      </ButtonsControl>
    </ImportWrapper>
  );
};

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SelectText = styled.p`
  text-align: center;
  padding: 50px 0 10px 0;
  color: ${({ theme }) => theme.font.weak};
  font-family: Blinker;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  margin: 0 auto;
  width: 70%;
`;

const RadioButtonWithNotificationWrapper = styled.div`
  color: ${themeVar('font.base')};
`;
